import axios from "axios";
import { authAPI } from "services/apis.service";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  loginUser,
  logoutUser,
  setMicrosoftUser,
} from "context/features/authSlice";
import { useUserData } from "features/User";
import { useSelector } from "react-redux";
import { clearStorage } from "utils/clear-storage.util";
import { redactAuthCode, stringifyValues } from "utils/client-log.util";

// Clever/ClassLink/NYC sign-up goes through a full-page OAuth redirect, so the
// signup page's invitationCode/referralCode/MetadataParams would otherwise be
// lost between "click Clever" and the provider redirecting back to us.
const OAUTH_SIGNUP_CONTEXT_KEY = "sooth-oauth-signup-context";

function persistOAuthSignupContext(context) {
  localStorage.setItem(OAUTH_SIGNUP_CONTEXT_KEY, JSON.stringify(context));
}

export function consumeOAuthSignupContext() {
  const raw = localStorage.getItem(OAUTH_SIGNUP_CONTEXT_KEY);
  localStorage.removeItem(OAUTH_SIGNUP_CONTEXT_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw);
  } catch {
    return {};
  }
}

export function useAuth({ handleLoading, toast }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const { setCategories } = useUserData();


  // `extra` is merged into additionalData (values stringified) so callers can
  // attach flow-specific diagnostics (attempt id, step, timing, etc.).
  async function logAuthError(error, provider, loginType = "Standard", level = "Error", extra = {}) {
    try {
      const safeError =
        typeof error === "string"
          ? { message: error, stack: null, response: null, code: "STRING_ERROR" }
          : error || {};

      let extractedEmail = "";
      const errorMessage = safeError?.response?.data || safeError?.message || "";
      if (typeof errorMessage === "string" && errorMessage.includes("Email domain is blocked.")) {
        const emailMatch = errorMessage.match(/Email: ([\w.%+-]+@[\w.-]+\.[a-zA-Z]{2,})/);
        if (emailMatch) {
          extractedEmail = emailMatch[1];
        }
      }

      const errorPayload = {
        level: level, // "Trace", "Debug", "Information", "Warning", "Error", "Critical"
        message: safeError?.message || "Unknown Error",
        source: `Auth/${loginType}/${provider}`,
        stackTrace: safeError?.stack || "",
        url: redactAuthCode(window.location.href),
        userAgent: window.navigator.userAgent,
        additionalData: {
          provider: provider,
          loginType: loginType,
          statusCode: String(safeError?.response?.status || 0),
          errorCode: safeError?.code || "",
          userEmail: extractedEmail,
          errorResponse: safeError?.response?.data
            ? typeof safeError.response.data === "object"
              ? JSON.stringify(safeError.response.data)
              : String(safeError.response.data)
            : "",
          timestamp: new Date().toISOString(),
          platform: window.navigator.platform,
          ...stringifyValues(extra),
        },
      };

      await axios.post(authAPI.logError, errorPayload);
    } catch (e) {
      console.error("Failed to log error to backend", e);
    }
  }

  function handleLoginUser(data) {
    dispatch(loginUser(data));
  }

  function handleMicrosoftUser(data) {
    dispatch(setMicrosoftUser(data));
  }

  function handleLogoutUser() {
    dispatch(logoutUser());
  }

  async function handleExternalLogin(token, provider, serviceUri, extraFields = {}) {
    try {
      handleLoading(true);
      const res = await axios.post(
        `${serviceUri}`,
        {
          token: token,
          provider: provider,
          ...extraFields,
        },
        {
          cache: false,
        }
      );
      localStorage.setItem("user", JSON.stringify(res.data));
      localStorage.setItem("login", true);

      //setData(res.data);
      setCategories(res.data.userId);

      //dispatch(loginUser({ user: res.data }));
      handleLoginUser({ user: res.data });
      localStorage.setItem("email", res.data.email);
      //dispatch(setMicrosoftUser(true));
      handleMicrosoftUser(true);

      handleLoading(false);
      handleLoading(false);
    } catch (e) {
      console.error(e);
      logAuthError(e, provider, "External/SSO");
      if (typeof e?.response?.data === "string" && e.response.data.includes("Email domain is blocked.")) {
        toast.error("Email domain is blocked.");
      } else {
        toast.error("Something went wrong");
      }
      handleLoading(false);

    }
  }

  function redirectToCleverOAuth() {
    const clientId = process.env.REACT_APP_API_PROD_CLEVER_CLIENTID;
    const redirectUri = encodeURIComponent(
      "https://app.sooth.fyi/signin/clever"
    );
    return `https://clever.com/oauth/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}`;
  }

  function redirectToClasslinkOAuth() {
    const clientId = process.env.REACT_APP_API_PROD_CLASSLINK_CLIENTID;
    const redirectUri = encodeURIComponent(window.origin + "/signin/classlink");
    return `https://launchpad.classlink.com/oauth2/v2/auth?scope=profile&redirect_uri=${redirectUri}&client_id=${clientId}&response_type=code`;
  }

  function redirecttoNYCOAuth() {
    const clientID = process.env.REACT_APP_NYC_CLIENTID;
    const redirectUri = encodeURIComponent(
      "https://staging-app.sooth.fyi/signin/nycenet"
    );
    return `https://idpstgcloud.nycenet.edu/oauth2/authorize?response_type=code&client_id=${clientID}&scope=openid%20profile%20email&redirect_uri=${redirectUri}`;
  }

  async function handleRedirectLogin(provider, apiUrl) {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");

    if (code && location.includes(provider)) {
      try {
        handleLoading(true);
        const signupContext = consumeOAuthSignupContext();
        const res = await axios.post(
          `${apiUrl}?code=${code}&provider=${provider}`,
          signupContext
        );
        localStorage.setItem("user", JSON.stringify(res.data));
        localStorage.setItem("login", true);

        setCategories(res.data.userId);

        handleLoginUser({ user: res.data });
        localStorage.setItem("email", res.data.email);

        handleLoading(false);
        handleLoading(false);
      } catch (error) {
        console.error("Error sending the code to backend:", error);
        logAuthError(error, provider, "Redirect/OAuth");
        if (toast != null) {
          if (typeof error?.response?.data === "string" && error.response.data.includes("Email domain is blocked.")) {
            toast.error("Email domain is blocked.");
          } else {
            toast.error(`There was an issue logging in with ${provider}.`);
          }
        }
      }
    }
  }

  async function handleLogout(microsoftUser, instance) {
    console.log("function user type", user.loginType);
    if (user.loginType === 6) {
      console.log("inside");
      const redirectUrl =
        "https://idpstgcloud.nycenet.edu/idaas/mtfim/sps/idaas/logout?post_logout_redirect_uri=https://staging-app.sooth.fyi/";
      window.location.href = redirectUrl;
      window.postMessage({ action: "logout" }, window.location.origin);
      return;
    }
    if (microsoftUser != null) {
      if (instance) {
        sessionStorage.clear();
      } else {
        console.error("MSAL instance is not available.");
      }
    }
    clearStorage();
    handleLogoutUser();
    window.location.replace("/");
  }

  return {
    handleExternalLogin,
    handleLoginUser,
    handleLogout,
    handleMicrosoftUser,
    handleRedirectLogin,
    redirectToClasslinkOAuth,
    redirectToCleverOAuth,
    redirecttoNYCOAuth,
    logAuthError,
    persistOAuthSignupContext,
  };
}
